import get from 'lodash/get';
import assign from 'lodash/assign';
import striptags from 'striptags';

import log from '../../../../services/logger_service';
import identityConfig from '../../../../config/identity';
import mergeContent from '../../../../utils/content_util';

import {
  buildAssetUrlWithPresets,
  imageSizesLookup,
  changeAssetDomain
} from '../../../../utils/cloudinary_asset_util';

/*
Validate your structured data page here (paste in entire view-source: https://search.google.com/structured-data/testing-tool/u/0/
*/
export default function parsely(context, location, firstLoad, unsanitizedURL) {
  const {
    // authorPageData,
    // categoryPageData,
    // personPageData,
    // tagPageData,
    // videoIndexPageData,
    authorAllFeed,
    categoryHomeList,
    companyPageData,
    giftGuideData,
    homeList,
    ibdPageData,
    jwPlaylistIndex,
    micYearPageData,
    micSectorPageData,
    mostCreativePeoplePageData,
    playlistAllFeedData,
    postData,
    productPageData,
    searchPageData,
    staticPageData,
    tagHomeList,
    videoPageData
  } = context;
  let { type } = context;
  const { config } = context;

  const siteConfig = identityConfig;
  const url = unsanitizedURL.includes('?amp=')
    ? unsanitizedURL.split('?')[0]
    : unsanitizedURL;

  if (siteConfig === undefined) {
    return '';
  }

  const articleType =
    get(postData, 'categories[0].name') === 'News' ? 'NewsArticle' : 'Article';

  let topicList = [];
  let articleTitle = '';
  let authorName = '';
  let articleSection = '';
  let thumbnail = '';
  let articleDate = '';
  let thumbnailCaption;
  let thumbnailWidth;
  let thumbnailHeight;
  let dateModified;
  const DESKTOP_SIZE = true;

  let livePostStructuredData = {};

  if (postData) {
    topicList = get(postData, 'tags');
    articleTitle = get(postData, 'title');
    authorName = get(postData, 'author.name');
    articleSection = get(postData, 'categories[0].slug'); // TODO, needs primary tag if exist
    thumbnail = buildAssetUrlWithPresets(
      changeAssetDomain(get(postData, 'featured_image.source')),
      imageSizesLookup.meta,
      DESKTOP_SIZE
    );

    if (/.gif$/.test(thumbnail)) {
      thumbnail = thumbnail.replace('f_auto', 'f_jpg');
    }

    articleDate = get(postData, 'structuredData.published');
    dateModified = get(postData, 'structuredData.modified');
    thumbnailCaption = striptags(get(postData, 'featured_image.caption') || '');
    thumbnailWidth = get(postData, 'featured_image.width') || 700;
    thumbnailHeight = get(postData, 'featured_image.height') || 700;
  }

  const siteData = {
    humanReadable: 'Fast Company',
    logo: {
      url:
        'https://assets.fastcompany.com/image/upload/v1510092328/fc-logo.png',
      width: '346',
      height: '60'
    }
  };
  const siteName = get(siteData, 'humanReadable');
  const logo = get(siteData, 'logo.url');
  const logoWidth = get(siteData, 'logo.width');
  const logoHeight = get(siteData, 'logo.height');

  const tags = topicList.map(tag => tag.name);
  // Set specific structured data for live posts
  if (postData) {
    const livePosts = get(postData, 'live.posts', []);
    const livePostsLength = livePosts.length;

    if (livePostsLength) {
      type = 'livePost';
      livePostStructuredData = {
        coverageStartTime: get(livePosts[livePostsLength - 1], 'published'),
        coverageEndTime: get(livePosts, '[0].published'),
        liveBlogUpdate: livePosts.map(post => ({
          '@type': 'BlogPosting',
          headline: articleTitle,
          author: {
            '@type': 'Person',
            name: get(post, 'author.name')
          },
          publisher: {
            '@type': 'Organization',
            name: siteName,
            logo: {
              '@type': 'ImageObject',
              url: logo,
              width: logoWidth,
              height: logoHeight
            }
          },
          url: `${url}#update-${get(post, 'id')}`,
          datePublished: get(post, 'published'),
          articleBody: striptags(mergeContent(get(post, 'content', [])))
        }))
      };
    }
  }

  const baseData = {
    '@context': 'https://schema.org'
  };

  const dupeLookUp = {};
  // We need a default date on landing pages in order for parsely to pick them up correctly.
  const defaultParselyDate = '2000-01-01T00:00:00Z';
  const typeLookup = {
    post: {
      '@type': articleType,
      headline: articleTitle,
      url,
      mainEntityOfPage: {
        '@id': url,
        '@type': 'WebPage'
      },
      thumbnailUrl: thumbnail,
      dateCreated: articleDate,
      datePublished: articleDate,
      dateModified,
      articleSection,
      creator: authorName,
      author: {
        '@type': 'Person',
        name: authorName
      },
      keywords: tags,
      image: {
        '@type': 'ImageObject',
        caption: thumbnailCaption,
        url: thumbnail,
        width: thumbnailWidth,
        height: thumbnailHeight
      },
      publisher: {
        '@type': 'Organization',
        name: siteName,
        logo: {
          '@type': 'ImageObject',
          url: logo,
          width: logoWidth,
          height: logoHeight
        }
      }
    },
    livePost: {
      '@type': 'LiveBlogPosting',
      url,
      headline: articleTitle,
      datePublished: articleDate,
      coverageStartTime: get(livePostStructuredData, 'coverageStartTime'),
      coverageEndTime: get(livePostStructuredData, 'coverageEndTime'),
      dateModified,
      publisher: {
        '@type': 'Organization',
        name: siteName,
        logo: {
          '@type': 'ImageObject',
          url: logo,
          width: logoWidth,
          height: logoHeight
        }
      },
      liveBlogUpdate: get(livePostStructuredData, 'liveBlogUpdate')
    },
    homepage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: homeList
        // we can only provide same-origin urls for structured data, so we should filter out
        // any urls that don't share the same domain as current homepage
        .filter(item => {
          // list item url is populated in the url field if it's scheduled in manage
          const listItemUrl = item.url || item.link;

          if (listItemUrl) {
            return listItemUrl.indexOf(url) !== -1;
          }

          return false;
        })
        .map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url:
            item.url ||
            `https://www.fastcompany.com/${get(item, 'id')}/${get(
              item,
              'slug'
            )}`
        }))
    },
    category: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: categoryHomeList.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url:
          item.url ||
          `https://www.fastcompany.com/${get(item, 'id')}/${get(item, 'slug')}`
      }))
    },
    tag: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: tagHomeList.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url:
          item.url ||
          `https://www.fastcompany.com/${get(item, 'id')}/${get(item, 'slug')}`
      }))
    },
    author: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: authorAllFeed.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url:
          item.url ||
          `https://www.fastcompany.com/${get(item, 'id')}/${get(item, 'slug')}`
      }))
    },
    personPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: []
    },
    mostCreativePeoplePage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(mostCreativePeoplePageData, 'data.rows', []).map(
        (item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `https://www.fastcompany.com/person/${item.url}`
        })
      )
    },
    ibdPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(ibdPageData, 'data.rows', []).map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://www.fastcompany.com/innovation-by-design/2017/category/${
          item.url
        }`
      }))
    },
    micYearPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(micYearPageData, 'data.rows', []).map(
        (item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `https://www.fastcompany.com/company/${item.url}`
        })
      )
    },
    micSectorPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(micSectorPageData, 'data.rows', []).map(
        (item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `https://www.fastcompany.com/most-innovative-companies/${get(
            micSectorPageData,
            'data.year'
          )}/sectors/${get(item, 'url')}`
        })
      )
    },
    giftGuidePage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(giftGuideData, 'data.rows', []).map(
        (item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `https://www.fastcompany.com/gift-guide/holiday-2017/category/${
            item.url
          }`
        })
      )
    },
    productPage: {
      '@type': 'NewsArticle',
      headline: get(productPageData, 'data.rows[0].name'),
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      mainEntityOfPage: url
    },
    companyPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(companyPageData, 'data.posts', []).map(
        (item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: item.link
        })
      )
    },
    videoIndexPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(jwPlaylistIndex, 'data.playlists', []).reduce(
        (accum, currVal) => {
          get(currVal, 'playlist', []).forEach(item => {
            const itemUrl = item.link;
            const itemJwId = item.mediaid;

            if (!dupeLookUp.hasOwnProperty(itemJwId)) {
              accum.push({
                '@type': 'ListItem',
                position: accum.length + 1,
                url: itemUrl
              });
              dupeLookUp[itemJwId] = true;
            }
          });
          return accum;
        },
        []
      )
    },
    videoPage: {
      '@type': 'VideoObject',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      name: get(videoPageData, 'data.playlist[0].title', ''),
      description: get(videoPageData, 'data.playlist[0].description', ''),
      thumbnailUrl: buildAssetUrlWithPresets(
        changeAssetDomain(
          get(videoPageData, 'data.playlist[0].featured_image.source', '')
        ),
        imageSizesLookup.meta,
        DESKTOP_SIZE
      ),
      uploadDate: get(
        videoPageData,
        'data.playlist[0].structuredData.published',
        ''
      ),
      transcript: get(videoPageData, 'data.videoSeoText', ''),
      duration: `PT${get(videoPageData, 'data.playlist[0].duration', '')}S`,
      contentUrl: get(videoPageData, 'data.playlist[0].sources[4].file', '')
    },
    videoPlaylistPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(playlistAllFeedData, 'data.playlist', []).map(
        (video, index) => {
          const itemUrl = video.link;

          return {
            '@type': 'ListItem',
            position: index + 1,
            url: itemUrl
          };
        }
      )
    },
    search: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(searchPageData, 'entry', []).map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: item.link
      }))
    },
    page: {
      '@type': 'NewsArticle',
      headline: get(staticPageData, 'title.rendered'),
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      mainEntityOfPage: url
    }
  };
  const JSONMeta = JSON.stringify(assign(baseData, typeLookup[type]));
  const lookUp = typeLookup[type];
  if (!lookUp) {
    log.error(
      `Structured Data Type Lookup: ${type} not found on url: ${get(
        config,
        'navHistory[0]'
      )}`
    );
  }

  return lookUp
    ? `<script type="application/ld+json">${JSONMeta}</script>`
    : '';
}
